// manage-employee.component.ts
import { ChangeDetectorRef, Component, ViewChild, OnDestroy, AfterViewInit, inject } from '@angular/core';
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatCardContent, MatCardTitle, MatCard, MatCardModule } from "@angular/material/card";
import { MatMenu, MatMenuModule } from "@angular/material/menu";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { MatFormField, MatFormFieldModule, MatLabel } from "@angular/material/form-field";
import { MatOption } from "@angular/material/core";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { MatAutocompleteModule } from '@angular/material/autocomplete'; // Required for mat-autocomplete and [matAutocomplete]
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { A11yModule } from "@angular/cdk/a11y";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ManageComplaintService } from '../../core/services/manage-complaints.service';
import { SimpleCodeValue } from '../../core/models/code-value.model';
import { EmployeeService } from '../../core/services/employee.service';
import {
  EmployeeDropdownOption,
  EmployeeLocationAllocation,
  EmployeeListItem,
  EmployeeSaveRequest,
  EmployeeSearchOptions,
  EmployeeSearchRequest,
  EmployeeSearchResult,
  EndEmployeeRequest
} from '../../core/models/employee.model';
import { AuthContext } from '../../auth/services/auth.context';
import { PublicService } from '../../core/services/public.service';
import { map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../../environments/environment';
import { FindLocationDialogComponent, FindLocationDialogData, FindLocationDialogResult } from '../user-management/manage-residents/find-location-dialog/find-location-dialog.component';
import { AssignedLocation } from '../../core/models/location.model';
import { LookupItem } from '../../core/services/lookup.service';
import { MatDialog } from '@angular/material/dialog';
import { UserManagementService } from '../user-management/user-management.service';


export function noSpecialCharsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Allow empty values → other validators (like required) will handle this
    if (value === null || value === '') {
      return null;
    }

    // allow only letters and spaces
    const valid = /^[a-zA-Z\s]*$/.test(value);

    return valid ? null : { invalidChars: true };
  };
}

@Component({
  selector: 'app-manage-employee',
  standalone: true,
  imports: [
    MatCardModule,
    MatTableModule,
    MatCheckboxModule,
    CommonModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatSelectModule,
    MatExpansionModule,
    MatIconModule,
    FormsModule,
    MatTabsModule,
    MatMenuModule,
    MatSnackBarModule,
    A11yModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule
  ],
  templateUrl: './manage-employee.component.html',
  styleUrls: ['./manage-employee.component.scss']
})
export class ManageEmployeeComponent implements AfterViewInit, OnDestroy {


  public showEndEmployeeModal: boolean = false; // Controls the visibility of the in-page modal
  public isFillingReplacement: boolean = false;
  public selectedReplacementId: any | null = null; // The ID selected in the modal dropdown

  employeeDetailsColumns: string[] = ['employeeName', 'trade', 'mobile', 'email', 'userName', 'actions'];
  dataSource = new MatTableDataSource<EmployeeListItem>([]);

  assignedLocationsArray: AssignedLocation[] = [];
  assignedLocations = new MatTableDataSource<AssignedLocation>(this.assignedLocationsArray);
  displayedColumns: string[] = ['areaName', 'QuarterNo', 'blockNumber', 'locationName', 'locationAssignedTo', 'actions', 'status'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('tabGroup') tabGroup: any;
  @ViewChild('assignedPaginator') assignedPaginator!: MatPaginator;

  private dialog = inject(MatDialog);

  selectedTabIndex = 0;
  hidePassword = true;
  isLoading = false;

  departments: SimpleCodeValue[] = [];
  roles: any = [];
  areas: SimpleCodeValue[] = [];
  lonavalaAreaOptions: SimpleCodeValue[] = [];
  selectedAreaIds = new Set<number>();
  areaAccessSearch = '';
  areaAccessFilter: 'ALL' | 'ASSIGNED' | 'UNASSIGNED' = 'ALL';
  canManageLonavalaAreaAccess = false;
  employeeHasUserLogin = false;
  areaOptionsLoading = false;
  employeeAreaAccessLoading = false;
  areaOptionsLoaded = false;
  employeeAreaAccessLoaded = false;
  areaAccessError = '';
  locationTypes: LookupItem[] = [];
  selectedResidentType: string = 'GROUP';

  employeeDetailsForm: FormGroup;
  loginForm: FormGroup
  requestForm: FormGroup;
  preFilled: boolean = false;
  showFormTabs: boolean = false;
  userNameExists: boolean = false;
  originalUsername: string | null = null;
  private destroy$ = new Subject<void>();
  employeeDetails: EmployeeSearchResult | null = null;
  employeeDropdownData: EmployeeDropdownOption[] = [];

  replacementEmployeeControl = new FormControl('');
  filteredEmployees: Observable<EmployeeDropdownOption[]> | undefined;
  public selectedReplacementLabel: string | null = null;
  isRegisteringNewEmployee: boolean = false;
  newEmployeeLabel: any;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private employeeService: EmployeeService,
    private UserManagementService: UserManagementService,
    private manageComplaintService: ManageComplaintService,
    private authContext: AuthContext,
    private snackBar: MatSnackBar,
    private publicService: PublicService
  ) {
    this.requestForm = this.fb.group({
      employeeName: new FormControl(''),
      employeeDepartment: new FormControl(''),
      mobileNumber: new FormControl(''),
      employeeTrade: new FormControl(''),
    });

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    this.employeeDetailsForm = this.fb.group({
      rank: [''],
      designation: [''],
      firstName: ['', [Validators.required, Validators.minLength(2), noSpecialCharsValidator()]],
      lastName: ['', noSpecialCharsValidator()],
      trade: [''],
      mobileNo: ['', [Validators.required, Validators.pattern('^[0-9]{10}$'), Validators.pattern(/^(?!.*(\d)\1{9})([1-9]\d{9})$/)]],
      email: ['', [Validators.required, Validators.pattern(emailRegex)]],
      officialNo: ['', [Validators.pattern('^[0-9]{10}$'), Validators.pattern(/^(?!.*(\d)\1{9})([1-9]\d{9})$/)]],
      receiveSMS: [false],
      receiveEmail: [false],
      department: ['', Validators.required],
    });

    this.loginForm = this.fb.group({
      userName: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(50), Validators.pattern('^[a-zA-Z0-9@._]+$')]],
      password: ['', [
        Validators.minLength(8),
        Validators.maxLength(12),
        Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$'),
      ]],
      roleName: [''],
      isActive: [false]
    });
  }

  ngOnInit(): void {
    const lookupValues = this.authContext.getLookups();
    const userInfo = this.authContext.getUserInfo();
    this.locationTypes = lookupValues['LOCATION_TYPE'] || [];
    this.canManageLonavalaAreaAccess = userInfo?.userType === 'ROLE_ADMIN'
      && userInfo.zoneIds?.includes(4) === true;
    if (this.canManageLonavalaAreaAccess) {
      this.loadLonavalaAreaOptions();
    }
    this.getUserZoneAreas();
    this.getDepartments();
    this.filteredEmployees = this.replacementEmployeeControl.valueChanges
      .pipe(
        startWith(''),
        map(value => this._filterEmployees(value || ''))
      );
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.assignedLocations.paginator = this.assignedPaginator;
    this.updatePasswordValidation();
  }


  private _filterEmployees(value: string | number): EmployeeDropdownOption[] {
    if (!this.employeeDropdownData) {
      return [];
    }

    const currentEmployeeId = this.employeeDetails?.employeeId;

    // 1. If the value is a number (meaning it's a pre-selected ID), return the matching employee, 
    // or the full list if the value is empty/not a search term.
    if (typeof value === 'number') {
      // Return only the employee matching the ID, plus filtering the current employee
      return this.employeeDropdownData.filter(emp =>
        emp.value === value && String(emp.value) !== String(currentEmployeeId)
      );
    }

    // 2. If the value is a string (meaning it's a search term or an empty string)
    const filterValue = (value || '').toLowerCase();

    const searchResults = this.employeeDropdownData.filter(emp =>
      emp.label.toLowerCase().includes(filterValue)
    );

    // Filter out the employee being ended
    return searchResults.filter(emp =>
      String(emp.value) !== String(currentEmployeeId)
    );
  }

  // manage-employee.component.ts

  // Function required by [displayWith]
  displayEmployeeName = (employeeId: number | string): string => {
    if (employeeId === null || employeeId === undefined || employeeId === '') return '';

    // Ensure the ID is numeric if it's supposed to be
    const idToFind = Number(employeeId);

    // Find the employee object in the full list using the ID
    const employee = this.employeeDropdownData.find(emp => emp.value === idToFind);

    // Return the label (name)
    return employee ? employee.label : '';
  };

  // 🟢 MODIFIED: onEmployeeSelected to explicitly set the control value to the ID (emp.value)
  onEmployeeSelected(event: any): void {
    this.selectedReplacementId = event.option.value;
    // Set the control value to the ID (emp.value) - displayWith will handle the name display
    this.replacementEmployeeControl.setValue(event.option.value);
    this.selectedReplacementLabel = event.option.viewValue;
  }

  onEndEmployee(): void {
    this.selectedReplacementId = null;
    this.showEndEmployeeModal = true;
    this.replacementEmployeeControl.setValue('');
    this.searchEmployee({ endEmployeeDepartmentId: this.employeeDetails?.departmentId });
  }

  onModalCancel(): void {
    this.showEndEmployeeModal = false;
    this.selectedReplacementId = null;
  }

  onRegisterNewEmployeeClick() {

    this.selectedReplacementId = 'NEW_EMPLOYEE_ID';
    this.onModalConfirm();
  }

  onModalConfirm(): void {
    const replacementId = this.selectedReplacementId;
    this.showEndEmployeeModal = false;

    const existingEmployeeId = this.employeeDetails?.employeeId;
    const existingDepartmentId = this.employeeDetails?.departmentId

    // Use the name for the notification message
    const replacementLabel = this.selectedReplacementLabel || replacementId;
    const existingEmployeeName = `${this.employeeDetails?.firstName} ${this.employeeDetails?.lastName}`;

    if (replacementId === 'NEW_EMPLOYEE_ID') {
      this.onCreateNewUser(existingDepartmentId ?? undefined);
      this.isFillingReplacement = true;
      this.selectedTabIndex = 0;
      this.snackBar.open('Please register the New Replacement Employee details, then click "Save" to re-open the handover dialog.', 'OK', { duration: 7000 });

    } else if (replacementId && existingEmployeeId) {

      const payload: EndEmployeeRequest = {
        existingEmployeeId: existingEmployeeId,
        newEmployeeId: replacementId
      };

      // UPDATED: Remove Employee ID from processing message
      this.snackBar.open(`Initiating handover from ${existingEmployeeName} to ${replacementLabel}...`, 'Processing', { duration: 5000 });

      this.employeeService.endEmployee(payload).subscribe({
        next: (response) => {
          // UPDATED: Remove Employee ID from success message
          this.snackBar.open(
            `✅ Handover successful! Responsibilities transferred to ${replacementLabel}.`,
            'Dismiss',
            { duration: 7000, panelClass: ['success-snackbar'] }
          );

          this.showFormTabs = false;
          this.isFillingReplacement = false;
          this.searchEmployee();

        },
        error: (err) => {
          // Error message remains technical, but ID is removed from the friendly part
          console.error('End Employee API failed:', err);
          this.snackBar.open(
            `❌ Handover failed for ${existingEmployeeName}: ${err.error?.message || 'Server error occurred.'}`,
            'Dismiss',
            { duration: 10000, panelClass: ['error-snackbar'] }
          );
        }
      });
    } else {
      this.snackBar.open('Error: Missing employee selection for handover.', 'Dismiss', { duration: 5000 });
    }
  }

  getUserZoneAreas() {
    this.UserManagementService.getUserAreas().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.areas = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  get areaAccessLoading(): boolean {
    return this.areaOptionsLoading || this.employeeAreaAccessLoading;
  }

  get areaAccessAppliesToCurrentEmployee(): boolean {
    return !this.preFilled || this.employeeHasUserLogin;
  }

  canManageAreasForEmployee(element: EmployeeListItem | EmployeeSearchResult): boolean {
    const employee = '__raw' in element ? element.__raw : element;
    return this.canManageLonavalaAreaAccess
      && employee?.employeeId != null
      && Boolean(employee?.userName);
  }

  get filteredLonavalaAreas(): SimpleCodeValue[] {
    const search = this.areaAccessSearch.trim().toLowerCase();
    return this.lonavalaAreaOptions.filter((area) => {
      const selected = this.isAreaSelected(area.code);
      const matchesFilter = this.areaAccessFilter === 'ALL'
        || (this.areaAccessFilter === 'ASSIGNED' && selected)
        || (this.areaAccessFilter === 'UNASSIGNED' && !selected);
      const matchesSearch = (area.value || '').toLowerCase().includes(search)
        || area.code.includes(search);
      return matchesFilter && matchesSearch;
    });
  }

  private loadLonavalaAreaOptions(): void {
    this.areaOptionsLoading = true;
    this.areaAccessError = '';
    this.employeeService.getLonavalaAreaOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (areas) => {
          this.lonavalaAreaOptions = areas;
          this.areaOptionsLoaded = true;
          this.areaOptionsLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.areaAccessError = 'Unable to load Lonavala areas.';
          this.areaOptionsLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private loadEmployeeAreaAccess(employeeId: number): void {
    this.employeeAreaAccessLoading = true;
    this.employeeAreaAccessLoaded = false;
    this.areaAccessError = '';
    this.selectedAreaIds.clear();
    this.employeeService.getEmployeeAreaAccess(employeeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (areaIds) => {
          this.selectedAreaIds = new Set(areaIds);
          this.employeeAreaAccessLoaded = true;
          this.employeeAreaAccessLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.areaAccessError = 'Unable to load this employee\'s area access.';
          this.employeeAreaAccessLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  isAreaSelected(areaCode: string): boolean {
    return this.selectedAreaIds.has(Number(areaCode));
  }

  setAreaSelected(areaCode: string, selected: boolean): void {
    const areaId = Number(areaCode);
    if (!Number.isInteger(areaId)) {
      return;
    }
    if (selected) {
      this.selectedAreaIds.add(areaId);
    } else {
      this.selectedAreaIds.delete(areaId);
    }
  }

  setVisibleAreasSelected(selected: boolean): void {
    const visibleAreas = [...this.filteredLonavalaAreas];
    visibleAreas.forEach((area) => this.setAreaSelected(area.code, selected));
  }

  onTabChange(event: { index: number }): void {
    const newTabIndex = event.index;
    const currentTabIndex = this.selectedTabIndex;

    // Only validate when moving forward
    if (newTabIndex > currentTabIndex) {
      let formToValidate: FormGroup | null = null;

      const loginTabIndex = this.canManageLonavalaAreaAccess ? 3 : 2;
      if (currentTabIndex === 0) {
        formToValidate = this.employeeDetailsForm;
      } else if (currentTabIndex === loginTabIndex) {
        formToValidate = this.loginForm;
      }

      if (formToValidate && formToValidate.invalid) {
        // Prevent tab change
        if (this.tabGroup) {
          this.tabGroup.selectedIndex = currentTabIndex;
        }
        formToValidate.markAllAsTouched();
        this.snackBar.open('Please fill out all required fields before proceeding.', 'OK', { duration: 3000 });
      } else {
        // allow backward navigation
        this.selectedTabIndex = newTabIndex;
      }
    } else {
      // allow backward navigation
      this.selectedTabIndex = newTabIndex;
    }
  }

  getRoleName() {
    const userInfo = this.authContext.getUserInfo();
    let userRole = '';
    if (userInfo) {
      userRole = userInfo.userType;
      if (userRole === 'ROLE_ADMIN') {
        this.roles = [
          { value: 'ROLE_JE', viewValue: 'Junior Engineer' },
          { value: 'ROLE_AGE', viewValue: 'Asst Garison Engineer' },
          { value: 'ROLE_ADMIN', viewValue: 'Admin Role' },
          { value: 'ROLE_GE', viewValue: 'Garrison Engineer' },
          { value: 'ROLE_CE', viewValue: 'Chief Engineer / Commander Works' }
        ];
      } else if (userRole === 'ROLE_AGE') {
        this.roles = [
          { value: 'ROLE_JE', viewValue: 'Junior Engineer' },
          { value: 'ROLE_AGE', viewValue: 'Asst Garison Engineer' }
        ];
      } else if (userRole === 'ROLE_JE') {
        this.roles = [
          { value: 'ROLE_JE', viewValue: 'Junior Engineer' }
        ];
      }
    }
  }

  onSave() {
    if (this.canManageLonavalaAreaAccess && this.areaAccessAppliesToCurrentEmployee) {
      if (!this.areaOptionsLoaded
        || (this.preFilled && this.employeeHasUserLogin && !this.employeeAreaAccessLoaded)
        || this.areaAccessLoading) {
        this.snackBar.open('Area access is still loading or could not be loaded.', 'OK', { duration: 3500 });
        return;
      }
      if (this.selectedAreaIds.size === 0) {
        this.selectedTabIndex = 2;
        if (this.tabGroup) {
          this.tabGroup.selectedIndex = 2;
        }
        this.snackBar.open('Select at least one Lonavala area for this employee.', 'OK', { duration: 3500 });
        return;
      }
    }

    if (this.employeeDetailsForm.invalid || this.loginForm.invalid) {
      this.employeeDetailsForm.markAllAsTouched();
      this.loginForm.markAllAsTouched();
      this.snackBar.open('Please ensure all mandatory fields are completed across all tabs before attempting to save.', 'OK', { duration: 3000 });
      return;
    }

    this.checkUsernameExists();
  }

  submitEmployee() {
    const details = this.employeeDetailsForm.getRawValue();
    const login = this.loginForm.getRawValue();

    let ivBase64: string | null = null;
    let encryptedUsername: string | null = null;
    let encryptedPassword: string | null = null;

    if (login && (login.userName || login.password)) {
      const secret = environment.ivBase64Secret;
      const sha256Key = CryptoJS.SHA256(secret);
      const iv = CryptoJS.lib.WordArray.random(16);
      ivBase64 = CryptoJS.enc.Base64.stringify(iv);

      if (login.userName) {
        encryptedUsername = CryptoJS.AES.encrypt(String(login.userName), sha256Key, { iv: iv }).toString();
      }

      // Only encrypt password if it exists and we are not in edit mode (or if it was explicitly typed)
      if (login.password) {
        encryptedPassword = CryptoJS.AES.encrypt(String(login.password), sha256Key, { iv: iv }).toString();
      }
    }

    const locationAllocationList: EmployeeLocationAllocation[] = this.assignedLocations?.filteredData
      ?.filter((location: any) => location.status !== 'Deleted')
      .map((location: any) => ({
        locationId: location.locationId || null,
        allocationId: location.allocationId || null
      })) || [];

    if (locationAllocationList.length === 0) {
      // Logic if no locations are assigned
    }


    let payload: EmployeeSaveRequest;
    if (this.isRegisteringNewEmployee) {
      payload = {
        employeeId: null,
        employeeNumber: null,
        rank: details.rank || null,
        designation: details.designation || null,
        firstName: details.firstName || null,
        lastName: details.lastName || null,
        trade: details.trade || null,
        mobileNumber: details.mobileNo || null,
        alternateMobileNumber: details.alternateMobileNumber || null,
        email: details.email || null,
        ivrLanguage: details.ivrLanguage || null,
        officeNumber: details.officialNo || null,
        homePhone: details.homePhone || null,
        sendSmsFlag: details.receiveSMS ? 'Y' : 'N',
        sendEmailFlag: details.receiveEmail ? 'Y' : 'N',
        startDate: details.startDate || null,
        endDate: details.endDate || null,
        loginRequest: {
          userName: encryptedUsername || null,
          password: encryptedPassword || null,
          instance: ivBase64
        },
        appGroup: login.roleName || details.appGroup || null,
        loginEnabled: login.isActive ? 'Y' : 'N',
        departmentId: details.departmentId ? Number(details.departmentId) : (details.department ? Number(details.department) : null),
        residentId: null,
        userGroupAssignmentId: null,
        employeeAssignmentId: null,
        createLogin: true,
        locationAllocationList: locationAllocationList,
        ...(this.canManageLonavalaAreaAccess && this.areaAccessAppliesToCurrentEmployee
          ? { areaIds: [...this.selectedAreaIds] }
          : {})
      };
    } else {
      payload = {
        employeeId: this.employeeDetails?.employeeId || null,
        employeeNumber: this.employeeDetails?.employeeNumber || null,
        rank: details.rank || null,
        designation: details.designation || null,
        firstName: details.firstName || null,
        lastName: details.lastName || null,
        trade: details.trade || null,
        mobileNumber: details.mobileNo || null,
        alternateMobileNumber: details.alternateMobileNumber || null,
        email: details.email || null,
        ivrLanguage: details.ivrLanguage || null,
        officeNumber: details.officialNo || null,
        homePhone: details.homePhone || null,
        sendSmsFlag: details.receiveSMS ? 'Y' : 'N',
        sendEmailFlag: details.receiveEmail ? 'Y' : 'N',
        startDate: details.startDate || null,
        endDate: details.endDate || null,
        loginRequest: {
          userName: encryptedUsername || null,
          password: encryptedPassword || null,
          instance: ivBase64
        },
        appGroup: login.roleName || details.appGroup || null,
        loginEnabled: login.isActive ? 'Y' : 'N',
        departmentId: details.departmentId ? Number(details.departmentId) : (details.department ? Number(details.department) : null),
        residentId: this.employeeDetails?.residentId || null,
        userGroupAssignmentId: this.employeeDetails?.userGroupAssignmentId || null,
        employeeAssignmentId: this.employeeDetails?.employeeAssignmentId || null,
        createLogin: true,
        locationAllocationList: locationAllocationList,
        ...(this.canManageLonavalaAreaAccess && this.areaAccessAppliesToCurrentEmployee
          ? { areaIds: [...this.selectedAreaIds] }
          : {})
      };
    }
    const newEmployeeFirstName = payload.firstName;
    const newEmployeeLastName = payload.lastName;
    this.isLoading = true;
    const actionName = this.preFilled ? 'Employee updated' : 'Employee saved';

    this.isLoading = true;
    const saveRequest = this.preFilled
      ? this.employeeService.updateEmployee(payload)
      : this.employeeService.saveEmployee(payload);
    saveRequest.subscribe({
      next: (res) => {
        this.isLoading = false;
        if (!res || res.valid === false) {
          const msg = res?.message || `Failed to ${this.preFilled ? 'update' : 'save'} employee.`;
          this.snackBar.open(msg, 'OK', { duration: 4000 });
          return;
        }

        if (this.isFillingReplacement) {
          const replacementEmployeeLabel = ` ${newEmployeeFirstName || ''} ${newEmployeeLastName || ''}`;
          this.resetEmployeeEditorState(true);
          this.newEmployeeLabel = replacementEmployeeLabel;
          this.isFillingReplacement = false;
          this.snackBar.open('Replacement Employee details saved successfully.', 'OK', { duration: 3000 });
          this.onEndEmployee();
          return;
        }

        this.resetEmployeeEditorState();
        this.isFillingReplacement = false;
        this.snackBar.open(`${actionName} successfully.`, 'OK', { duration: 3000 });
        this.searchEmployee();
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Operation failed', err);
        const message = err?.error?.message || `Failed to ${this.preFilled ? 'update' : 'save'} employee. Please try again.`;
        this.snackBar.open(message, 'OK', { duration: 4000 });
      }
    });
  }

  private resetEmployeeEditorState(preserveEmployeeDetails = false): void {
    this.employeeDetailsForm.reset();
    this.loginForm.reset();
    this.assignedLocationsArray = [];
    this.assignedLocations.data = [];
    this.selectedAreaIds.clear();
    this.employeeHasUserLogin = false;
    this.employeeAreaAccessLoaded = false;
    this.employeeAreaAccessLoading = false;
    this.areaAccessSearch = '';
    this.areaAccessFilter = 'ALL';
    this.areaAccessError = '';
    this.originalUsername = null;
    this.newEmployeeLabel = null;
    this.showFormTabs = false;
    this.preFilled = false;
    this.isRegisteringNewEmployee = false;
    this.selectedTabIndex = 0;
    if (!preserveEmployeeDetails) {
      this.employeeDetails = null;
    }
    this.updatePasswordValidation();

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    if (this.assignedPaginator) {
      this.assignedPaginator.pageIndex = 0;
    }

    this.cdr.detectChanges();
  }


  onCancel() {
    this.employeeDetailsForm.reset();
    this.loginForm.reset();
    this.selectedAreaIds.clear();
    this.employeeHasUserLogin = false;
    this.showFormTabs = false;

    setTimeout(() => {
      if (this.paginator) {
        this.dataSource.paginator = this.paginator;
        this.paginator.pageIndex = 0;
        this.cdr.detectChanges();
      }
    }, 0);
  }


  openLocationPopup(): void {
    const dialogData: FindLocationDialogData = {
      areas: this.areas,
      currentAssignedLocations: this.preFilled ? this.assignedLocationsArray : [],
      selectedResidentType: this.selectedResidentType,
      locationTypes: this.locationTypes
    };

    const dialogRef = this.dialog.open(FindLocationDialogComponent, {
      width: '80%',
      maxWidth: '100%',
      height: '90%',
      maxHeight: '100%',
      data: dialogData,
      disableClose: false,
      autoFocus: false
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: FindLocationDialogResult) => {
      if (result) {
        // Update assigned locations with the result from dialog
        this.assignedLocationsArray = result.assignedLocations;
        this.assignedLocations.data = [...this.assignedLocationsArray];
        // RE-LINK paginator after data change
        if (this.assignedPaginator) {
          this.assignedLocations.paginator = this.assignedPaginator;
          this.assignedPaginator.pageIndex = 0;
        }
        console.log('Updated Assigned Locations from dialog:', this.assignedLocations.data);
      }
    });
  }

  removeLocation(id: string) {
    console.log('Removing location with id:', id);

    this.assignedLocationsArray = this.assignedLocationsArray.map(loc =>
      loc.id === id ? { ...loc, status: 'Deleted' } : loc
    );

    // Update the data source data instead of recreating
    this.assignedLocations.data = [...this.assignedLocationsArray];

    console.log('Updated Assigned Locations:', this.assignedLocations.data);
  }

  getSelectedLocationsCount(): number {
    return this.assignedLocationsArray.filter((location) => location.status !== 'Deleted').length;
  }

  basicInfo(element: any, tab: string): void {
    this.preFilled = true;
    this.showFormTabs = true;
    this.isRegisteringNewEmployee = false;
    this.fillFormWithResidentData(element);
    // Update password validators for edit mode
    this.updatePasswordValidation();

    // Set the selected tab
    switch (tab) {
      case 'employeeDetails':
        this.selectedTabIndex = 0;
        break;
      case 'manageLocation':
        this.selectedTabIndex = 1;
        break;
      case 'manageAreas':
        this.selectedTabIndex = 2;
        break;
      case 'manageLogin':
        this.selectedTabIndex = this.canManageLonavalaAreaAccess ? 3 : 2;
        break;
    }
  }

  fillFormWithResidentData(element: any) {
    this.departments = [];

    const raw = element && element.__raw ? element.__raw : element;
    console.log(`Filling form with data (raw):`, raw);
    this.employeeDetails = raw;
    this.selectedAreaIds.clear();
    this.employeeHasUserLogin = !!raw.userName;
    if (this.canManageLonavalaAreaAccess && this.employeeHasUserLogin && raw.employeeId) {
      this.loadEmployeeAreaAccess(raw.employeeId);
    }

    this.employeeDetailsForm.patchValue({
      rank: raw.rank || '',
      designation: raw.designation || '',
      firstName: raw.firstName || '',
      lastName: raw.lastName || '',
      trade: raw.trade || '',
      mobileNo: raw.mobileNumber || '',
      email: raw.email || '',
      officialNo: raw.officeNumber || raw.officialNumber || '',
      receiveSMS: (raw.sendSmsFlag === 'Y'),
      receiveEmail: (raw.sendEmailFlag === 'Y'),
    });

    this.getRoleName();

    this.loginForm.patchValue({
      userName: raw.userName || '',
      roleName: raw.appGroup || '',
      isActive: (raw.loginEnabled === 'Y')
    });

    console.log(raw.locationAllocationList);


    if (raw.locationAllocationList && raw.locationAllocationList.length > 0) {
      console.log('raw Location Allocation List:', raw.locationAllocationList);

      this.assignedLocationsArray = raw.locationAllocationList.map((location: any) => ({
        areaName: location.areaName,
        quarterNumber: location.quarterNumber,
        blockNumber: location.blockNumber,
        locationName: location.locationName,
        locationAssignedTo: raw.firstName + ' ' + raw.lastName,
        id: location.allocationId,
        allocationId: location.allocationId,
        locationId: location.locationId,
        status: 'Assigned'
      } as AssignedLocation));
    } else {
      this.assignedLocationsArray = [];
    }

    this.assignedLocations.data = [...this.assignedLocationsArray];
    // RE-LINK paginator
    if (this.assignedPaginator) {
      this.assignedLocations.paginator = this.assignedPaginator;
      this.assignedPaginator.pageIndex = 0;
    }

    console.log('Filled Assigned Locations:', this.assignedLocations.data);


    // remember original username so edit/save can skip re-check when unchanged
    this.originalUsername = raw.userName || null;

    this.getDepartments(() => {
      const deptValue = (raw.departmentId !== null && raw.departmentId !== undefined)
        ? String(raw.departmentId)
        : (raw.department !== null && raw.department !== undefined) ? String(raw.department) : '';

      this.employeeDetailsForm.patchValue({
        department: deptValue,
        departmentId: deptValue
      });
      this.cdr.detectChanges();
    });
  }

  checkUsernameExists(): void {
    if (this.preFilled) {
      const currentUsername = this.loginForm.get('userName')?.value;
      const originalUsername = this.originalUsername;

      // If username wasn't changed, proceed directly
      if (currentUsername === originalUsername) {
        this.submitEmployee();
        return;
      }
    }

    const userName = this.loginForm.get('userName')?.value;
    const userNameControl = this.loginForm.get('userName');
    const token: string | null = localStorage.getItem('accessToken');

    if (userName) {
      this.publicService.checkUsernameAvailability(userName, token || '').pipe(takeUntil(this.destroy$)).subscribe({
        next: (response: any) => {
          // The backend returns valid:true when username is available in the residents flow
          // In that codebase userNameExists was used differently; here we proceed when available
          const available = response?.valid;
          if (available) {
            this.submitEmployee();
          } else {
            userNameControl?.setErrors({ usernameExists: true });
            this.snackBar.open('Username already exists. Please choose a different username.', 'Okay');
          }
        },
        error: (error: unknown) => {
          console.error('API error while checking username:', error);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // 🟢 MODIFIED: onCreateNewUser to set default role and isRegisteringNewEmployee flag
  onCreateNewUser(departmentIdToPreload?: string | number) {
    this.employeeDetailsForm.reset();
    this.loginForm.reset();
    this.selectedAreaIds.clear();
    this.employeeHasUserLogin = true;
    this.employeeAreaAccessLoaded = true;
    this.areaAccessError = '';
    this.areaAccessSearch = '';
    this.areaAccessFilter = 'ALL';
    this.assignedLocations.data = [];
    this.showFormTabs = true;
    this.selectedTabIndex = 0;
    this.preFilled = false;
    this.departments = [];
    this.getDepartments();
    this.roles = [];
    this.getRoleName();


    const defaultRole = (departmentIdToPreload !== undefined && departmentIdToPreload !== null) ? 'ROLE_JE' : null;

    this.isRegisteringNewEmployee = (departmentIdToPreload !== undefined && departmentIdToPreload !== null); // 🟢 Set flag if department ID is passed (transfer context)

    this.employeeDetailsForm.patchValue({
      receiveSMS: true,
      receiveEmail: true
    });

    this.loginForm.patchValue({
      isActive: true,
      roleName: defaultRole
    });

    this.getDepartments(() => {
      const departmentControl = this.employeeDetailsForm.get('department');

      if (departmentControl) {
        departmentControl.enable();

        if (departmentIdToPreload !== undefined && departmentIdToPreload !== null) {
          const departmentValueString = String(departmentIdToPreload);
          departmentControl.setValue(departmentValueString);
        } else {
          departmentControl.setValue(null);
        }
      }

      this.cdr.detectChanges();
    });

    this.updatePasswordValidation();
  }

  updatePasswordValidation(): void {
    const passwordControl = this.loginForm.get('password');
    if (passwordControl) {
      if (this.preFilled) {
        // In edit mode, clear the validators
        passwordControl.clearValidators();
      } else {
        // In new entry mode, set the validators for required, minLength, and maxLength
        passwordControl.setValidators([
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(12)
        ]);
      }
      // Re-evaluate the control's validation status
      passwordControl.updateValueAndValidity();
    }
    if (this.preFilled) {
      this.loginForm.get('userName')?.disable();
    } else {
      this.loginForm.get('userName')?.enable();
    }
    console.log('updatePasswordValidation preFilled:', this.preFilled);
  }



  getDepartments(callback?: () => void) {
    this.manageComplaintService.getMyZoneAssignableDepartments().subscribe({
      next: (response) => {
        this.departments = response;
        this.cdr.detectChanges();
        if (callback) {
          try { callback(); } catch (e) { console.error('getDepartments callback error', e); }
        }
      },
      error: (error) => {
        console.error('POST error:', error);
        if (callback) {
          try { callback(); } catch (e) { console.error('getDepartments callback error', e); }
        }
      },
    });
  }


  searchEmployee(options?: EmployeeSearchOptions): void {
    const isEndEmployeeSearch = options !== undefined;
    const endEmployeeDepartmentId = options?.endEmployeeDepartmentId;

    if (isEndEmployeeSearch && (endEmployeeDepartmentId === null || endEmployeeDepartmentId === undefined || endEmployeeDepartmentId === '')) {
      this.snackBar.open('Could not determine the selected employee department.', 'OK', { duration: 3000 });
      return;
    }

    this.isLoading = true;

    const selectedDepartmentId = this.requestForm.value.employeeDepartment;
    const payload: EmployeeSearchRequest = {
      employeeName: isEndEmployeeSearch ? null : this.requestForm.value.employeeName || null,
      mobileNumber: isEndEmployeeSearch ? null : this.requestForm.value.mobileNumber || null,
      departmentId: isEndEmployeeSearch
        ? Number(endEmployeeDepartmentId)
        : selectedDepartmentId ? Number(selectedDepartmentId) : null,
      employeeTrade: isEndEmployeeSearch ? null : this.requestForm.value.employeeTrade || null,
      offset: 0,
      limit: 100
    };

    this.employeeService.searchEmployees(payload).subscribe({
      next: (result) => {
        const records = result?.data ?? [];

        // Exclude the employee that is currently being ended
        const currentEmployeeId = this.employeeDetails?.employeeId;
        const filteredRecords = records.filter(emp => emp.employeeId !== currentEmployeeId);

        this.employeeDropdownData = filteredRecords
          .map(emp => ({
            value: emp.employeeId,
            label: ` ${emp.firstName || ''} ${emp.lastName || ''}`
          }));
        this.replacementEmployeeControl.updateValueAndValidity();

        if (this.newEmployeeLabel) {
          const newEmployee: any = this.employeeDropdownData.find(emp =>
            emp.label.trim() === this.newEmployeeLabel?.trim()
          );

          if (newEmployee) {
            // Set the form control value to the employee ID (value)
            this.replacementEmployeeControl.setValue(newEmployee.value);
            // Important: Also update your internal ID tracker if you have one
            this.selectedReplacementId = newEmployee.value;
          }

          this.newEmployeeLabel = null; // Clear the flag after use
        }

        this.dataSource.data = records.map((emp): EmployeeListItem => ({
          rank: emp.rank || '',
          employeeName: `${emp.firstName || ''} ${emp.lastName || ''}`,
          trade: emp.trade || '',
          mobile: emp.mobileNumber || emp.mobile || '',
          officeNumber: emp.officeNumber || '',
          email: emp.email || '',
          userName: emp.userName || '',
          designation: emp.designation || '',
          actions: '',
          __raw: emp ? JSON.parse(JSON.stringify(emp)) : emp
        }));

        this.isLoading = false;

        // Ensure change detection runs after setting form control value
        this.cdr.detectChanges();

        if (this.paginator) this.paginator.pageIndex = 0;
        if (this.assignedPaginator) this.assignedPaginator.pageIndex = 0;

        if (this.paginator) {
          this.dataSource.paginator = this.paginator;
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Search failed', err);
      }
    });
  }

}